import React, { useState, useEffect } from 'react';
import { getPublicSpec } from '../utils/api';
import CustomApiViewer from './CustomApiViewer';
import yaml from 'js-yaml';

const getViewerHtml = (type, spec) => {
  if (type === 'swagger') {
    return `<!DOCTYPE html>
<html>
<head>
  <link rel="stylesheet" type="text/css" href="https://unpkg.com/swagger-ui-dist@5.9.0/swagger-ui.css" />
</head>
<body>
  <div id="swagger-ui"></div>
  <script src="https://unpkg.com/swagger-ui-dist@5.9.0/swagger-ui-bundle.js"></script>
  <script>
    window.onload = () => {
      SwaggerUIBundle({
        spec: ${JSON.stringify(spec)},
        dom_id: '#swagger-ui',
        presets: [SwaggerUIBundle.presets.apis, SwaggerUIBundle.presets.standalone]
      });
    };
  </script>
</body>
</html>`;
  } else if (type === 'elements') {
    return `<!DOCTYPE html>
<html>
<head>
  <script src="https://unpkg.com/@stoplight/elements@7.15.0/web-components.min.js"></script>
  <link rel="stylesheet" href="https://unpkg.com/@stoplight/elements@7.15.0/styles.min.css">
</head>
<body>
  <elements-api id="docs" router="hash" layout="sidebar"></elements-api>
  <script>
    setTimeout(() => {
      const el = document.getElementById('docs');
      el.apiDescriptionDocument = JSON.stringify(${JSON.stringify(spec)});
    }, 100);
  </script>
</body>
</html>`;
  } else if (type === 'redoc') {
    return `<!DOCTYPE html>
<html>
<head>
  <script src="https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js"></script>
</head>
<body>
  <div id="redoc-container"></div>
  <script>
    window.onload = () => {
      Redoc.init(${JSON.stringify(spec)}, {}, document.getElementById('redoc-container'));
    };
  </script>
</body>
</html>`;
  } else if (type === 'rapidoc') {
    return `<!DOCTYPE html>
<html>
<head>
  <script type="module" src="https://unpkg.com/rapidoc/dist/rapidoc-min.js"></script>
</head>
<body>
  <rapi-doc theme="light" render-style="read" spec-url="data:application/json;base64,${btoa(JSON.stringify(spec))}"></rapi-doc>
</body>
</html>`;
  } else if (type === 'scalar') {
    return `<!DOCTYPE html>
<html>
<head>
  <title>Scalar API Reference</title>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
</head>
<body>
  <script id="api-reference" type="application/json">
    ${JSON.stringify(spec)}
  </script>
  <script src="https://cdn.jsdelivr.net/npm/@scalar/api-reference"></script>
</body>
</html>`;
  }
};

const PublicSpecViewer = () => {
  const pathParts = window.location.pathname.split('/').filter(p => p);
  const title = decodeURIComponent(pathParts[2]);
  const version = pathParts[3];
  const params = new URLSearchParams(window.location.search);
  const viewer = params.get('viewer') || 'custom';
  
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSpec();
  }, [title, version]);

  const loadSpec = async () => {
    try {
      const data = await getPublicSpec(title, version);
      const content = data.content;
      const parsed = typeof content === 'string' 
        ? (content.trim().startsWith('{') ? JSON.parse(content) : yaml.load(content))
        : content;
      setSpec(parsed);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={styles.container}>Loading...</div>;
  if (error) return <div style={styles.container}>Error: {error}</div>;
  if (!spec) return <div style={styles.container}>Spec not found</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>{spec.info?.title || 'API Specification'}</h1>
          {spec.info?.version && <span style={styles.version}>v{spec.info.version}</span>}
        </div>
        <select 
          value={viewer} 
          onChange={(e) => window.location.search = `?viewer=${e.target.value}`}
          style={styles.select}>
          <option value="custom">Deviant Viewer</option>
          <option value="swagger">Swagger UI</option>
          <option value="elements">Stoplight Elements</option>
          <option value="redoc">Redoc</option>
          <option value="rapidoc">RapiDoc</option>
          <option value="scalar">Scalar</option>
        </select>
      </div>

      <div style={styles.viewerContainer}>
        {viewer === 'custom' && <CustomApiViewer spec={spec} darkMode={false} />}
        {viewer !== 'custom' && <iframe srcDoc={getViewerHtml(viewer, spec)} style={styles.iframe} />}
      </div>
    </div>
  );
};

const styles = {
  container: {
    height: '100vh',
    background: '#f6f8fa',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 40px',
    background: '#fff',
    borderBottom: '1px solid #d0d7de',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600',
    color: '#24292f',
  },
  version: {
    background: '#0969da',
    color: '#fff',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
  },
  select: {
    background: '#fff',
    color: '#24292f',
    border: '1px solid #d0d7de',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  viewerContainer: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
};

export default PublicSpecViewer;

// Add to App.js routing:
// import PublicSpecViewer from './components/PublicSpecViewer';
// Add route: {window.location.pathname.startsWith('/public/specs/') && <PublicSpecViewer />}
